import {CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL} from "../../api/model/common";
import {ActivityFlightState, Leg} from "../../types-enums/activity-flight";
import {LegForUi} from "../../components/activity/flight/choose-flight/ChooseFlight";
import {TRAVEL_CLASSES, TravelClass} from "../../screens/activity/activity-form/flight";

export type FailedLegsProps = Leg[][];

/**
 * Retrieves the failed or successful legs of a flight based on the provided response status.
 *
 * This function iterates through the emissions data associated with a flight and selects legs
 * (pairs of consecutive departure and arrival points) that match the specified response status.
 * It excludes legs that involve an "own airport transfer" at the arrival point.
 *
 * @param {ActivityFlightState} flight - The flight object, including its legs and other relevant metadata.
 * @param {CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL} responseStatus - The response status used to filter the legs.
 * @returns {FailedLegsProps} An array of failed or successful legs, with each leg consisting of a pair of departure and arrival data.
 */
export const getFlightFailedOrSuccessfulLegs = (flight: ActivityFlightState, responseStatus: CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL): FailedLegsProps => {
    const {legs} = flight
    let failedLegs: FailedLegsProps = []
    const {common} = flight
    const {totalEmissions} = common
    totalEmissions?.forEach(leg => {
        if (leg.status === responseStatus) {
            const {index} = leg
            const departure = legs[index]
            const arrival = legs[index + 1]
            if (!arrival.ownAirportTransfer && departure && arrival) failedLegs.push([departure, arrival])
        }
    })
    return failedLegs
}
/**
 * Retrieves the first leg of the array of legs.
 *
 * This function takes an array of legs and returns the first element
 * in the array. If the array is empty, the result is `undefined`.
 *
 * @param {Leg[]} legs - The array of leg objects.
 * @returns {Leg | undefined} The first leg in the array, or `undefined` if the array is empty.
 */
export const getFirstLeg = (legs: Leg[]): Leg | undefined => legs.slice(0)[0]

/**
 * Retrieves the last leg from an array of legs.
 *
 * @param {Leg[]} legs - An array of leg objects.
 * @returns {Leg | undefined} The last leg in the array, or undefined if the array is empty.
 */
export const getLastLeg = (legs: Leg[]): Leg | undefined => legs.slice(-1)[0]

/**
 * Retrieves the first UI leg from an array of legs.
 *
 * This function takes an array of `LegForUi` objects and returns the first
 * element in the array. It slices the array and extracts the first element.
 *
 * @param {LegForUi[]} legs - An array of `LegForUi` objects representing the legs.
 * @returns {LegForUi | undefined} The first `LegForUi` object in the array, or undefined if the array is empty.
 */
export const getFirstUiLeg = (legs: LegForUi[]): LegForUi | undefined => legs.slice(0)[0]

/**
 * Retrieves the stopover legs for UI purposes from a given array of legs.
 *
 * This function processes an array of flight legs and returns an array of stopover legs. A stopover is defined as the intermediate legs of the journey, excluding the first and last ones.
 *
 * @param {LegForUi[]} legs - An array of legs representing the journey. Each leg is represented as a `LegForUi` object.
 * @returns {LegForUi[]} - An array containing the stopover legs if the length of the input array is greater than 2; otherwise, returns an empty array.
 */
export const getUiStopover = (legs: LegForUi[]): LegForUi[] => {
    if (legs.length > 2) return legs.slice(1, legs.length - 2)
    return []
}
/**
 * Retrieves the name of a travel class as a string based on the provided travel class value.
 * If the travel class value does not match any predefined travel class, returns null.
 *
 * @param {TravelClass} travelClass - The travel class value to find the corresponding name for.
 * @returns {string | null} The name of the travel class if found, otherwise null.
 */
export const getTravelClassString = (travelClass: TravelClass): string | null => TRAVEL_CLASSES.find(tc => tc.value === travelClass)?.name ?? null